// Phase 3 — GWAS (Genome-Wide Association Study).
//
// Niranjan (Feb 2026) — the EMMAX MLM with PCA + VanRaden kinship
// correction. Three-file upload: numeric genotype matrix +
// phenotype table + optional marker map (for per-chromosome
// Manhattan plotting). Defaults to single synthetic chromosome
// when map is absent.
//
// `Phase3ResultPanels` is exported for reuse on /results/<id>.
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Loader2, CheckCircle2, ExternalLink, Download, FileText, Target } from 'lucide-react';
import { API } from '@/App';
import { sha256OfFile, recordAnalysis } from '@/utils/molecularSessionRegistry';
import { StepHeader, ParamLabel, RuntimeEstimate, useCsvHeader, autoSelectColumn, estimateRuntime } from './uploadCardHelpers';

const TIPS_P3 = {
  genotype_col: "Column in the phenotype file that holds the genotype/line ID. Must match the row index of the genotype file.",
  n_pcs: "Number of principal components to include as the Q (population structure) matrix. 3-5 typically captures most stratification.",
  alpha: "Family-wise error rate. The Bonferroni threshold = α / n_markers; SNPs with p ≤ this threshold are flagged as significant.",
  apply_marker_qc: "Apply MAF / missingness / heterozygosity filters before fitting the MLM. Recommended unless data is already QC'd.",
};

const GWAS_COLOR = '#8B5CF6';


export function Phase3UploadCard({ onResult, analysisId, exporting, onExport }) {
  const [genoFile, setGenoFile] = useState(null);
  const [phenoFile, setPhenoFile] = useState(null);
  const [mapFile, setMapFile] = useState(null);
  const [genotypeCol, setGenotypeCol] = useState('Genotype');
  const [nPcs, setNPcs] = useState(3);
  const [alpha, setAlpha] = useState(0.05);
  const [applyQc, setApplyQc] = useState(true);
  const [running, setRunning] = useState(false);
  const [loadingSample, setLoadingSample] = useState(false);
  const [result, setResult] = useState(null);

  const onPickGeno = (e) => { const f = e.target.files?.[0]; if (f) setGenoFile(f); };
  const onPickPheno = (e) => { const f = e.target.files?.[0]; if (f) setPhenoFile(f); };
  const onPickMap = (e) => { const f = e.target.files?.[0]; if (f) setMapFile(f); };

  const loadSampleData = async () => {
    setLoadingSample(true);
    try {
      const [g, p] = await Promise.all([
        axios.get(`${API}/molecular/sample-data/genotype`, { responseType: 'blob', withCredentials: true }),
        axios.get(`${API}/molecular/sample-data/phenotype`, { responseType: 'blob', withCredentials: true }),
      ]);
      setGenoFile(new File([g.data], 'molecular_phase3_genotype.csv', { type: 'text/csv' }));
      setPhenoFile(new File([p.data], 'molecular_phase3_phenotype.csv', { type: 'text/csv' }));
      setMapFile(null);
      toast.success('Sample data loaded — 100 lines × 500 SNPs × 3 traits.');
    } catch (e) {
      toast.error('Sample data fetch failed.');
    } finally { setLoadingSample(false); }
  };

  const runAnalysis = async () => {
    if (!genoFile || !phenoFile) {
      toast.error('Both genotype and phenotype files are required.');
      return;
    }
    setRunning(true);
    setResult(null);
    const fd = new FormData();
    fd.append('genotype_file', genoFile);
    fd.append('phenotype_file', phenoFile);
    if (mapFile) fd.append('map_file', mapFile);
    fd.append('genotype_col', genotypeCol);
    fd.append('n_pcs', String(nPcs));
    fd.append('alpha', String(alpha));
    fd.append('apply_marker_qc', String(applyQc));
    try {
      const { data } = await axios.post(`${API}/molecular/gwas`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      const r = data?.results?.molecular_gwas;
      setResult(r);
      onResult(data);
      try {
        const genotypeHash = await sha256OfFile(genoFile);
        recordAnalysis(data.id, { phase: 'gwas', genotypeHash });
      } catch { /* best-effort */ }
      toast.success('GWAS complete!');
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Run failed';
      toast.error(`GWAS failed: ${msg}`);
    } finally { setRunning(false); }
  };

  const phenoHeaders = useCsvHeader(phenoFile);
  React.useEffect(() => {
    if (!phenoHeaders.length) return;
    if (!phenoHeaders.includes(genotypeCol)) {
      const guess = autoSelectColumn(phenoHeaders, ['^genotype$', '^line$', '^entry$', '^id$']);
      if (guess) setGenotypeCol(guess);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phenoHeaders.join(',')]);

  return (
    <div className="space-y-5" data-testid="mol-phase3-card">
      {/* STEP 1 — UPLOAD */}
      <div>
        <StepHeader step="1" title="Upload your data" hint="Genotype + phenotype + optional marker map" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <FileSlot label="Genotype file (0/1/2 numeric matrix)" file={genoFile} onPick={onPickGeno} testid="mol-phase3-genotype-file"
            accept=".csv,.xlsx,.xls" hint="Rows = individuals; Cols = SNPs; Values in {0, 1, 2, NaN}." />
          <FileSlot label="Phenotype file" file={phenoFile} onPick={onPickPheno} testid="mol-phase3-phenotype-file"
            accept=".csv,.xlsx,.xls" hint="Cols: Genotype | Rep (optional) | Trait1 | Trait2 …" />
          <FileSlot label="Marker map (optional)" file={mapFile} onPick={onPickMap} testid="mol-phase3-map-file"
            accept=".csv,.xlsx,.xls" hint="Cols: marker, chromosome, position_bp. Used for per-chromosome Manhattan." />
        </div>
      </div>

      {/* STEP 2 — CONFIGURE */}
      <div>
        <StepHeader step="2" title="Configure analysis" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <ColumnSelect label="Genotype column" value={genotypeCol} onChange={setGenotypeCol}
            options={phenoHeaders} testid="mol-phase3-genotype-col" hint={TIPS_P3.genotype_col} />
          <NumInput label="Number of PCs (Q-matrix)" value={nPcs} setValue={setNPcs} step={1} min={0} max={10}
            testid="mol-phase3-npcs" hint={TIPS_P3.n_pcs} />
          <NumInput label="α (Bonferroni)" value={alpha} setValue={setAlpha} step={0.01} min={0.001} max={0.5}
            testid="mol-phase3-alpha" hint={TIPS_P3.alpha} />
          <ToggleInput label="Apply marker QC" value={applyQc} setValue={setApplyQc}
            testid="mol-phase3-apply-qc" hint={TIPS_P3.apply_marker_qc} />
        </div>
      </div>

      {/* STEP 3 — RUN */}
      <div>
        <StepHeader step="3" title="Run analysis" />
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={loadSampleData} disabled={loadingSample || running}
            className="flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3.5 py-2 border disabled:opacity-50"
            style={{ borderColor: GWAS_COLOR + '4D', color: GWAS_COLOR, background: GWAS_COLOR + '0D' }}
            data-testid="mol-phase3-sample-data">
            {loadingSample ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Try Sample Data
          </button>
          <button type="button" onClick={runAnalysis} disabled={running || !genoFile || !phenoFile}
            className="flex items-center gap-1.5 text-sm font-semibold rounded-lg px-4 py-2 text-white disabled:opacity-50 shadow-sm"
            style={{ background: GWAS_COLOR }}
            data-testid="mol-phase3-run">
            {running ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {running ? 'Running EMMAX MLM…' : 'Run GWAS'}
          </button>
          <RuntimeEstimate testid="mol-phase3-runtime-estimate"
            estimate={estimateRuntime({ phase: 'gwas', nIndiv: 100, nMarkers: 500 })} />
        </div>
      </div>

      {result && (
        <div className="space-y-5 fade-in" data-testid="mol-phase3-result">
          <div className="flex items-center justify-between gap-3 rounded-xl px-4 py-3"
            style={{ background: GWAS_COLOR + '1A', border: `1px solid ${GWAS_COLOR}4D` }}
            data-testid="analysis-complete-banner">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex items-center justify-center rounded-full shrink-0" style={{ width: 28, height: 28, background: GWAS_COLOR, color: 'white' }}>
                <CheckCircle2 size={16} />
              </div>
              <div className="min-w-0">
                <div className="truncate" style={{ fontSize: 13, fontWeight: 700, color: GWAS_COLOR }}>GWAS complete</div>
                <div className="truncate" style={{ fontSize: 11, color: '#5E5A56' }}>Open the dedicated Results page for Manhattan plots and significant-SNP tables.</div>
              </div>
            </div>
            {analysisId && (
              <button onClick={() => window.location.href = `/results/${analysisId}`}
                className="flex items-center gap-1.5 shrink-0 transition-all"
                style={{ fontSize: 13, fontWeight: 700, borderRadius: 8, padding: '8px 18px',
                  border: `1px solid ${GWAS_COLOR}`, color: 'white', background: GWAS_COLOR,
                  boxShadow: `0 2px 6px ${GWAS_COLOR}66` }}
                data-testid="view-full-results">
                <ExternalLink size={13} /> View Full Results
              </button>
            )}
          </div>

          <Phase3ResultPanels result={result} />

          {analysisId && onExport && (
            <div className="flex items-center gap-2 flex-wrap pt-2" data-testid="export-bar">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mr-1">Export</span>
              {['docx', 'xlsx', 'pdf'].map(fmt => (
                <button key={fmt} onClick={() => onExport(analysisId, fmt)} disabled={!!exporting}
                  className="flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-3 py-1.5 transition-all border shadow-sm disabled:opacity-50"
                  style={{ borderColor: GWAS_COLOR + '4D', color: GWAS_COLOR, background: GWAS_COLOR + '0D' }}
                  data-testid={`export-${fmt}-button`}>
                  {exporting === `${analysisId}-${fmt}` ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
                  {fmt.toUpperCase()}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function FileSlot({ label, file, onPick, testid, accept, hint }) {
  return (
    <label className="block rounded-xl border-2 border-dashed border-[#E8E6E1] hover:border-[#8B5CF6] p-4 cursor-pointer transition-colors bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        <FileText size={14} style={{ color: GWAS_COLOR }} />
        <span className="text-xs font-semibold text-slate-700">{label}</span>
      </div>
      <input type="file" accept={accept} onChange={onPick} className="hidden" />
      <div className="text-xs text-slate-500 mb-2">{hint}</div>
      <div className="flex items-center gap-2 mt-2">
        {file ? (
          <span className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded font-mono truncate max-w-full"
            style={{ background: GWAS_COLOR + '1A', color: GWAS_COLOR }}>
            <CheckCircle2 size={11} /> {file.name} ({(file.size / 1024).toFixed(1)} KB)
          </span>
        ) : (
          <span className="text-[11px] text-slate-400 italic">Click to select a CSV / XLSX file</span>
        )}
      </div>
    </label>
  );
}


function ColInput({ label, value, onChange, testid, hint }) {
  return (
    <div className="block">
      <ParamLabel label={label} hint={hint} testid={testid} htmlFor={testid} />
      <input id={testid} type="text" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2"
        style={{ '--tw-ring-color': GWAS_COLOR + '4D' }}
        data-testid={testid} />
    </div>
  );
}


function ColumnSelect({ label, value, onChange, options, allowNone, testid, hint }) {
  if (!options || options.length === 0) {
    return <ColInput label={label} value={value} onChange={onChange} testid={testid} hint={hint} />;
  }
  return (
    <div className="block">
      <ParamLabel label={label} hint={hint} testid={testid} htmlFor={testid} />
      <select id={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 bg-white"
        data-testid={testid}>
        {allowNone && <option value="">(none)</option>}
        {options.map(o => <option key={o} value={o}>{o}</option>)}
        {value && !options.includes(value) && (
          <option value={value}>{value} (custom)</option>
        )}
      </select>
    </div>
  );
}


function NumInput({ label, value, setValue, step, min, max, testid, hint }) {
  return (
    <div className="block">
      <ParamLabel label={label} hint={hint} testid={testid} htmlFor={testid} />
      <input id={testid} type="number" value={value} onChange={(e) => setValue(parseFloat(e.target.value) || min)}
        step={step} min={min} max={max}
        className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2"
        data-testid={testid} />
    </div>
  );
}


function ToggleInput({ label, value, setValue, testid, hint }) {
  return (
    <div className="block">
      <ParamLabel label={label} hint={hint} testid={testid} htmlFor={testid} />
      <button id={testid} type="button" onClick={() => setValue(!value)}
        className="w-full text-sm border rounded-md px-2 py-1.5 text-left transition-colors"
        style={value
          ? { borderColor: GWAS_COLOR, background: GWAS_COLOR + '1A', color: GWAS_COLOR, fontWeight: 600 }
          : { borderColor: '#E2E8F0', background: 'white', color: '#64748B' }}
        data-testid={testid}>
        {value ? '✓ Enabled' : '✗ Disabled'}
      </button>
    </div>
  );
}


export function Phase3ResultPanels({ result }) {
  if (!result) return null;
  const summary = result.input_summary || {};
  const perTrait = result.per_trait || {};
  const manhattanCharts = result.manhattan_charts || {};
  const qqCharts = result.qq_charts || {};
  const pca = result.pca || {};

  return (
    <div className="space-y-5">
      {result.interpretation && (
        <div className="rounded-xl px-4 py-3 text-sm text-slate-700 leading-relaxed"
          style={{ border: `1px solid ${GWAS_COLOR}4D`, background: '#FAF5FF' }}
          data-testid="mol-phase3-interpretation">
          {result.interpretation}
        </div>
      )}

      {/* Input + summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SummaryCard title="Input" testid="mol-phase3-input-summary" rows={[
          ['Individuals', summary.n_individuals],
          ['Markers tested', summary.n_markers],
          ['PCs (Q-matrix)', summary.n_pcs_used],
          ['α', summary.alpha],
          ['Bonferroni threshold', summary.bonferroni_threshold?.toExponential(2)],
          ['Marker QC applied', summary.marker_qc_applied ? 'Yes' : 'No'],
        ]} />
        <SummaryCard title="Per-trait λ inflation" testid="mol-phase3-lambda-summary" rows={
          Object.entries(perTrait).map(([trait, r]) => [
            trait, r.error ? 'error' : `λ = ${r.lambda_gc} (${r.n_significant_snps} sig SNPs)`,
          ])
        } />
      </div>

      {/* PCA charts */}
      {pca.scree_chart && (
        <ChartCard title="PCA — scree plot (population structure)" b64={pca.scree_chart} testid="mol-phase3-scree-chart" />
      )}
      {pca.biplot_chart && (
        <ChartCard title="PCA biplot — PC1 × PC2" b64={pca.biplot_chart} testid="mol-phase3-biplot-chart" />
      )}

      {/* Per-trait sections: Manhattan + QQ + sig-SNP table */}
      {Object.entries(perTrait).map(([trait, r]) => {
        if (r.error) {
          return (
            <div key={trait} className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <strong>{trait}:</strong> {r.error}
            </div>
          );
        }
        return (
          <div key={trait} className="space-y-3 rounded-xl border-2 p-4" style={{ borderColor: GWAS_COLOR + '4D' }} data-testid={`mol-phase3-trait-${trait}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Target size={14} style={{ color: GWAS_COLOR }} />
                <span className="text-sm font-bold text-slate-800">{trait}</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-600">
                <span>λ = <span className="font-mono font-semibold">{r.lambda_gc}</span></span>
                <span>·</span>
                <span><span className="font-mono font-semibold">{r.n_significant_snps}</span> sig SNPs</span>
                <span>·</span>
                <span>Lead: <span className="font-mono font-semibold">{r.lead_marker?.marker}</span> (p = {r.lead_marker?.p_value?.toExponential(2)})</span>
              </div>
            </div>
            {manhattanCharts[trait] && (
              <ChartCard title="Manhattan plot" b64={manhattanCharts[trait]} testid={`mol-phase3-manhattan-${trait}`} />
            )}
            {qqCharts[trait] && (
              <ChartCard title="QQ plot" b64={qqCharts[trait]} testid={`mol-phase3-qq-${trait}`} />
            )}
            {r.significant_snps && r.significant_snps.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`mol-phase3-sigtable-${trait}`}>
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Significant SNPs (Bonferroni p &lt; {r.bonferroni_threshold?.toExponential(2)})
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50/50 text-slate-600 text-xs">
                      <tr>
                        <th className="text-left px-3 py-2">Marker</th>
                        <th className="text-left px-3 py-2">Chr</th>
                        <th className="text-right px-3 py-2">Position</th>
                        <th className="text-right px-3 py-2">MAF</th>
                        <th className="text-right px-3 py-2">β</th>
                        <th className="text-right px-3 py-2">p-value</th>
                        <th className="text-right px-3 py-2">−log10 p</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.significant_snps.map((s, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-3 py-1.5 font-mono text-xs">{s.marker}</td>
                          <td className="px-3 py-1.5 text-xs">{s.chromosome}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-xs">{s.position}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-xs">{s.maf}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-xs">{s.beta?.toFixed(4)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-xs">{s.p_value?.toExponential(2)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-xs font-semibold" style={{ color: GWAS_COLOR }}>{s.neg_log10_p}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


function SummaryCard({ title, rows, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={testid}>
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600">
        {title}
      </div>
      <dl className="divide-y divide-slate-100">
        {rows.map(([k, v], i) => (
          <div key={i} className="px-4 py-1.5 grid grid-cols-2 text-sm">
            <dt className="text-slate-500 text-xs">{k}</dt>
            <dd className="text-right text-slate-800 tabular-nums font-mono text-xs">{v ?? '—'}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}


function ChartCard({ title, b64, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={testid}>
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600">
        {title}
      </div>
      <div className="p-4 flex justify-center">
        <img src={`data:image/png;base64,${b64}`} alt={title} className="max-w-full" />
      </div>
    </div>
  );
}
